import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PauseCircle, PlayCircle, Trash2, Snowflake } from "lucide-react";
import { formatDMY, todayISO } from "@/lib/dateFormat";
import { toast } from "sonner";


/** Pausa con fechas (viajes): congela las sesiones pendientes, bloquea la
 *  asistencia en esas fechas y evita que se facture un ciclo nuevo. */
export default function PauseEnrollmentDialog({ open, onOpenChange, enrollment, studentName, onSaved }) {
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState("");
  const [openEnded, setOpenEnded] = useState(true);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resumeDate, setResumeDate] = useState(todayISO());

  const pauses = useMemo(
    () => [...(enrollment?.pauses || [])].sort((a, b) => (b.from_date || "").localeCompare(a.from_date || "")),
    [enrollment],
  );
  const activePause = pauses.find((p) => !p.to_date || p.to_date >= todayISO());

  const createPause = async () => {
    if (!from) { toast.error("Elegí la fecha en que arranca la pausa"); return; }
    setSaving(true);
    try {
      await api.post(`/enrollments/${enrollment.id}/pause`, {
        from_date: from,
        to_date: openEnded ? null : (to || null),
        reason: reason || null,
        notify,
      });
      toast.success("Programa en pausa");
      setReason("");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo pausar");
    } finally {
      setSaving(false);
    }
  };

  const resume = async (pauseId) => {
    setSaving(true);
    try {
      await api.post(`/enrollments/${enrollment.id}/pauses/${pauseId}/resume`, {
        resume_date: resumeDate || todayISO(),
        notify,
      });
      toast.success("Programa reactivado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reactivar");
    } finally {
      setSaving(false);
    }
  };

  const removePause = async (pauseId) => {
    setSaving(true);
    try {
      await api.delete(`/enrollments/${enrollment.id}/pauses/${pauseId}`);
      toast.success("Pausa eliminada");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pause-enrollment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PauseCircle className="h-4 w-4 text-amber-600" /> Suspender asistencia y cobro
          </DialogTitle>
          <DialogDescription>
            {studentName} sigue en el grupo. Las clases dentro de la pausa quedan en gris, la profe
            no puede marcarle asistencia y no consumen el ciclo: las sesiones que le queden se
            corren hasta su regreso y no se factura un ciclo nuevo.
          </DialogDescription>
        </DialogHeader>

        {activePause ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3" data-testid="pause-active-box">
            <p className="text-sm text-amber-900 flex items-center gap-2">
              <Snowflake className="h-4 w-4" />
              En pausa desde el <strong>{formatDMY(activePause.from_date)}</strong>
              {activePause.to_date ? <> hasta el <strong>{formatDMY(activePause.to_date)}</strong></> : " (sin fecha de regreso)"}
            </p>
            {activePause.reason && <p className="text-xs text-amber-800">Motivo: {activePause.reason}</p>}
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs">Regresa el</Label>
                <Input type="date" value={resumeDate} onChange={(e) => setResumeDate(e.target.value)}
                       data-testid="pause-resume-date" className="h-9" />
              </div>
              <Button onClick={() => resume(activePause.id)} disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid="pause-resume-confirm">
                <PlayCircle className="h-4 w-4 mr-1" /> Reactivar
              </Button>
              <Button variant="outline" onClick={() => removePause(activePause.id)} disabled={saving}
                      className="text-red-700" data-testid="pause-delete-active">
                <Trash2 className="h-4 w-4 mr-1" /> Borrar pausa
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                       data-testid="pause-from-input" />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={to} disabled={openEnded} min={from}
                       onChange={(e) => setTo(e.target.value)} data-testid="pause-to-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={openEnded} onChange={(e) => setOpenEnded(e.target.checked)}
                     data-testid="pause-open-ended" />
              No sabe cuándo regresa (pausa abierta)
            </label>
            <div>
              <Label>Motivo</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Viaje fuera del país, motivos de salud…"
                        data-testid="pause-reason-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                     data-testid="pause-notify" />
              Avisarle al estudiante por correo
            </label>
          </div>
        )}

        {pauses.length > 0 && (
          <div className="border-t pt-3" data-testid="pause-history">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Historial de pausas</p>
            <ul className="space-y-1.5 max-h-40 overflow-auto">
              {pauses.map((p) => (
                <li key={p.id} className="text-xs text-slate-600 flex items-start justify-between gap-2"
                    data-testid={`pause-row-${p.id}`}>
                  <span>
                    <strong>{formatDMY(p.from_date)}</strong>
                    {p.to_date ? ` → ${formatDMY(p.to_date)}` : " → sin fecha de regreso"}
                    {p.reason ? ` · ${p.reason}` : ""}
                  </span>
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-red-600"
                          onClick={() => removePause(p.id)} disabled={saving}
                          data-testid={`pause-delete-${p.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          {!activePause && (
            <Button onClick={createPause} disabled={saving} className="bg-amber-600 hover:bg-amber-700"
                    data-testid="pause-confirm">
              <PauseCircle className="h-4 w-4 mr-1" /> Pausar programa
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
